import { IconFolderAdd, IconUserAdd, IconFolderOpen } from "@cynaps/icons";
import { Spinner, Tooltip as UITooltip } from "@cynaps/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useUpdatePageTitle } from "@cynaps/core";
import { useAuth } from "@cynaps/core/providers/AuthProvider";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

import { useAPI } from "../../providers/ApiProvider";
import { CreateProject } from "../CreateProject/CreateProject";
import type { Page } from "../types/Page";
import styles from "./HomePage.module.css";
import { InviteMembersDialog } from "../../components/InviteMembersDialog";

const PROJECTS_TO_SHOW = 10;

const actions = [
  {
    title: "Create Project",
    icon: IconFolderAdd,
    type: "createProject",
  },
  {
    title: "Invite Members",
    icon: IconUserAdd,
    type: "inviteMembers",
  },
] as const;

type Action = (typeof actions)[number]["type"];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
    },
  },
};

export const HomePage: Page = () => {
  const api = useAPI();
  const { user } = useAuth();
  const [creationDialogOpen, setCreationDialogOpen] = useState(false);
  const [invitationOpen, setInvitationOpen] = useState(false);

  useUpdatePageTitle("Home");
  const { data, isFetching, isSuccess, isError } = useQuery({
    queryKey: ["projects", { page_size: 10 }],
    async queryFn() {
      return api.callApi<{ results: APIProject[]; count: number }>("projects", {
        params: { page_size: PROJECTS_TO_SHOW },
      });
    },
  });

  const handleActions = (action: Action) => {
    return () => {
      switch (action) {
        case "createProject":
          setCreationDialogOpen(true);
          break;
        case "inviteMembers":
          setInvitationOpen(true);
          break;
      }
    };
  };

  const { chartData, summaryStats, barChartData } = useMemo(() => {
    if (!data?.results) return { chartData: [], summaryStats: null, barChartData: [] };
    
    let finished = 0;
    let unfinished = 0;
    let totalTasks = 0;
    const totalProjects = data.count || data.results.length;
    const barData: any[] = [];
    
    data.results.forEach((p, index) => {
      const f = p.finished_task_number ?? 0;
      const t = p.task_number ?? 0;
      finished += f;
      totalTasks += t;
      unfinished += Math.max(0, t - f);
      
      if (index < 5) {
        barData.push({
          name: p.title.length > 12 ? p.title.substring(0, 12) + '...' : p.title,
          completed: f,
          pending: Math.max(0, t - f),
        });
      }
    });
    
    const completionRate = totalTasks > 0 ? Math.round((finished / totalTasks) * 100) : 0;
    
    const stats = {
      projects: totalProjects,
      tasks: totalTasks,
      completionRate: completionRate
    };
    
    if (finished === 0 && unfinished === 0) return { chartData: [], summaryStats: stats, barChartData: barData };
    
    return {
      chartData: [
        { name: 'Completed', value: finished, color: '#22c55e' },
        { name: 'Pending', value: unfinished, color: '#334155' }
      ],
      summaryStats: stats,
      barChartData: barData
    };
  }, [data]);

  return (
    <main className={styles.homePage}>
      <motion.div className={styles.container} variants={containerVariants} initial="hidden" animate="visible">
        <motion.div className={styles.header} variants={itemVariants}>
          <h1 className={styles.welcomeTitle}>Dashboard</h1>
          <p className={styles.welcomeSubtitle}>Manage your annotation projects and team</p>
        </motion.div>

        <motion.div className={styles.actionsGrid} variants={itemVariants}>
          {actions.map((action) => {
            return (
              <motion.button
                key={action.title}
                className={styles.actionButton}
                onClick={handleActions(action.type)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <action.icon />
                {action.title}
              </motion.button>
            );
          })}
        </motion.div>

        <div className={styles.mainGrid}>
          <motion.section className={styles.analyticsSection} variants={itemVariants}>
            {summaryStats && (
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Total Projects</span>
                  <span className={styles.statValue}>{summaryStats.projects}</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Total Tasks</span>
                  <span className={styles.statValue}>{summaryStats.tasks}</span>
                </div>
                <div className={styles.statCard}>
                  <span className={styles.statLabel}>Completion Rate</span>
                  <span className={styles.statValue}>{summaryStats.completionRate}%</span>
                </div>
              </div>
            )}
            
            {chartData.length > 0 && (
              <div className={styles.chartsGrid}>
                <div className={styles.card}>
                  <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Overall Progress</h2>
                  </div>
                  <div style={{ width: '100%', height: 220 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={75}
                          paddingAngle={4}
                          dataKey="value"
                          stroke="none"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          contentStyle={{ backgroundColor: "#1e293b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff" }}
                          itemStyle={{ color: "#f8fafc" }}
                        />
                        <Legend verticalAlign="bottom" height={36} iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {barChartData.length > 0 && (
                  <div className={styles.card}>
                    <div className={styles.cardHeader}>
                      <h2 className={styles.cardTitle}>Recent Projects Progress</h2>
                    </div>
                    <div style={{ width: '100%', height: 220 }}>
                      <ResponsiveContainer>
                        <BarChart data={barChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                          <XAxis 
                            dataKey="name" 
                            stroke="rgba(255, 255, 255, 0.3)" 
                            tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis 
                            stroke="rgba(255, 255, 255, 0.3)" 
                            tick={{ fill: "rgba(255, 255, 255, 0.4)", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <RechartsTooltip 
                            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff" }}
                            itemStyle={{ color: "#f8fafc" }}
                          />
                          <Bar dataKey="completed" name="Completed" stackId="a" fill="#22c55e" radius={[0, 0, 4, 4]} />
                          <Bar dataKey="pending" name="Pending" stackId="a" fill="#334155" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.section>

          <motion.section className={styles.mainSection} variants={itemVariants}>
            <div className={styles.card}>
              {data && data?.count > 0 && (
                <div className={styles.cardHeader}>
                  <h2 className={styles.cardTitle}>Recent Projects</h2>
                  <a href="/projects" className={styles.cardViewAll}>
                    View All
                  </a>
                </div>
              )}

              {isFetching ? (
                <div className={styles.loadingContainer}>
                  <Spinner />
                </div>
              ) : isError ? (
                <div className={styles.errorContainer}>// ERROR: Failed to load projects</div>
              ) : isSuccess && data && data.results.length === 0 ? (
                <motion.div
                  className={styles.emptyState}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className={styles.emptyStateIcon}>
                    <IconFolderOpen />
                  </div>
                  <h3 className={styles.emptyStateTitle}>Create your first project</h3>
                  <p className={styles.emptyStateDescription}>
                    Import your data and configure the labeling interface to begin annotation
                  </p>
                  <motion.button
                    className={styles.createButton}
                    onClick={() => setCreationDialogOpen(true)}
                    aria-label="Create new project"
                    whileHover={{
                      y: -2,
                      boxShadow: "0 8px 24px rgba(139, 92, 246, 0.3)",
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Create Project
                  </motion.button>
                </motion.div>
              ) : isSuccess && data && data.results.length > 0 ? (
                <div className={styles.projectsList}>
                  {data.results.map((project, index) => {
                    return <ProjectCard key={project.id} project={project} index={index} />;
                  })}
                </div>
              ) : null}
            </div>
          </motion.section>
        </div>
      </motion.div>

      {creationDialogOpen && <CreateProject onClose={() => setCreationDialogOpen(false)} />}
      <InviteMembersDialog
        isOpen={invitationOpen}
        onClose={() => setInvitationOpen(false)}
        organizationId={user?.active_organization}
      />
    </main>
  );
};

HomePage.title = "Dashboard";
HomePage.path = "/dashboard";
HomePage.exact = true;

function ProjectCard({
  project,
  index,
}: {
  project: APIProject;
  index: number;
}) {
  const finished = project.finished_task_number ?? 0;
  const total = project.task_number ?? 0;
  const progress = (total > 0 ? finished / total : 0) * 100;
  const white = "#FFFFFF";
  const color = project.color && project.color !== white ? project.color : "#8b5cf6";

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
    >
      <Link
        to={`/projects/${project.id}`}
        className={styles.projectCard}
        style={{ "--project-color": color } as React.CSSProperties}
        data-external
      >
        <div className={styles.projectCardContent}>
          <div className={styles.projectInfo}>
            <UITooltip title={project.title}>
              <span className={styles.projectTitle}>{project.title}</span>
            </UITooltip>
            <div className={styles.projectStats}>
              {finished} / {total} tasks completed
            </div>
          </div>
          <div className={styles.projectProgress}>
            <span className={styles.projectProgressLabel}>{total > 0 ? Math.round((finished / total) * 100) : 0}%</span>
            <div className={styles.projectProgressBar}>
              <div className={styles.projectProgressFill} style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
